import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import HomePage from './HomePage';
import TechnologyPage from './TechnologyPage';
import ServicesPage from './ServicesPage';
import TrackRecordPage from './TrackRecordPage';
import ContactPage from './ContactPage';
import OnePageHeader from '../../components/layout/OnePageHeader';
import OnePageFooter from '../../components/layout/OnePageFooter';

const SECTIONS = [
	{ id: 'home', labelKey: 'nav.home' },
	{ id: 'technology', labelKey: 'nav.technology' },
	{ id: 'services', labelKey: 'nav.services' },
	{ id: 'footprint', labelKey: 'nav.trackRecord' },
	{ id: 'contact', labelKey: 'contact.pageTitle' },
];

const OnePage = () => {
	const { t, isRTL } = useLanguage();
	const [active, setActive] = useState(SECTIONS[0].id);

	useEffect(() => {
		const observerOptions = { root: null, rootMargin: '0px', threshold: 0.45 };
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setActive(entry.target.getAttribute('data-section-id'));
				}
			});
		}, observerOptions);

		SECTIONS.forEach((s) => {
			const el = document.querySelector(`[data-section-id="${s.id}"]`);
			if (el) observer.observe(el);
		});

		return () => observer.disconnect();
	}, []);

	// fallback / complement: update active based on scroll position (robust when observer misses)
	useEffect(() => {
		let rafId = null;
		const onScroll = () => {
			if (rafId) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				const headerOffset = headerHeight + 8; // small buffer
				let best = { id: null, distance: Infinity };
				SECTIONS.forEach((s) => {
					const el = document.querySelector(`[data-section-id="${s.id}"]`);
					if (!el) return;
					const rect = el.getBoundingClientRect();
					const distance = Math.abs(rect.top - headerOffset);
					if (distance < best.distance) best = { id: s.id, distance };
				});
				if (best.id && best.id !== active) setActive(best.id);
			});
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		// trigger once to set initial active correctly
		onScroll();

		return () => {
			window.removeEventListener('scroll', onScroll);
			if (rafId) cancelAnimationFrame(rafId);
		};
	}, [active]);

	// listen for header's onepage-scroll events (dispatched by Header buttons)
	useEffect(() => {
		const handler = (e) => {
			if (e && e.detail) scrollTo(e.detail);
		};
		window.addEventListener('onepage-scroll', handler);
		return () => window.removeEventListener('onepage-scroll', handler);
	}, []);

	const scrollTo = (id) => {
		const el = document.querySelector(`[data-section-id="${id}"]`);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const headerHeight = 80; // px (match global Header height)

	useEffect(() => {
		// dispatch initial active on mount (in case observer found something immediately)
		if (active) window.dispatchEvent(new CustomEvent('onepage-active', { detail: active }));
	}, []);

	useEffect(() => {
		// when active changes, notify the global Header so it can highlight appropriately
		if (active) window.dispatchEvent(new CustomEvent('onepage-active', { detail: active }));
	}, [active]);

	return (
		<div dir={isRTL ? 'rtl' : 'ltr'}>
			{/* Use the copied header tailored for the one-page view (separate from global Header) */}
			<OnePageHeader />

			{/* Sections: wrap each original page in a container with data-section-id and scroll-margin to avoid header overlap */}
			<div data-section-id="home" style={{ scrollMarginTop: `${headerHeight + 16}px` }}>
				<HomePage />
			</div>

			<div data-section-id="technology" style={{ scrollMarginTop: `${headerHeight + 16}px` }}>
				<TechnologyPage />
			</div>

			<div data-section-id="services" style={{ scrollMarginTop: `${headerHeight + 16}px` }}>
				<ServicesPage />
			</div>

			<div data-section-id="footprint" style={{ scrollMarginTop: `${headerHeight + 16}px` }}>
				<TrackRecordPage />
			</div>

			<div data-section-id="contact" style={{ scrollMarginTop: `${headerHeight + 16}px` }}>
				<ContactPage />
			</div>

			{/* One-page footer copy that scrolls for quick links */}
			<OnePageFooter />
		</div>
	);
};

export default OnePage;
